import React, { ReactElement } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Skeleton,
  Stack
} from '@mui/material';

export interface AdminTableColumn {
  key: string;
  label: string;
  width?: string;
  minWidth?: number;
  sortable?: boolean;
  icon?: React.ReactNode;
  sortIcon?: string;
  onClick?: () => void;
}

interface AdminTableProps<T = unknown> {
  columns: AdminTableColumn[];
  data: T[];
  loading?: boolean;
  skeletonRows?: number;
  renderRow: (item: T, index?: number) => React.ReactNode;
  emptyState?: React.ReactNode;
}

export function AdminTable<T = unknown>({
  columns,
  data,
  loading = false,
  skeletonRows = 5,
  renderRow,
  emptyState
}: AdminTableProps<T>): ReactElement {
  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        borderRadius: 2, 
        boxShadow: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        '& .MuiTable-root': {
          minWidth: 1000,
          tableLayout: 'fixed'
        }
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ 
            bgcolor: 'action.hover',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid',
              borderBottomColor: 'divider'
            }
          }}>
            {columns.map((column) => (
              <TableCell 
                key={column.key}
                sx={{ 
                  fontWeight: 600, 
                  py: 2,
                  color: 'text.primary',
                  bgcolor: 'transparent',
                  width: column.width,
                  minWidth: column.minWidth,
                  cursor: column.sortable ? 'pointer' : 'default',
                  '&:hover': column.sortable ? {
                    bgcolor: 'action.hover'
                  } : {}
                }}
                onClick={column.onClick}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  {column.icon}
                  <span>{column.label}</span>
                  {column.sortIcon && (
                    <span style={{ fontSize: '12px', marginLeft: '4px' }}>
                      {column.sortIcon}
                    </span>
                  )}
                </Stack>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            // Skeleton loading rows
            Array.from({ length: skeletonRows }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {columns.map((column) => (
                  <TableCell key={column.key} sx={{ py: 2, width: column.width, minWidth: column.minWidth }}>
                    <Skeleton variant="text" width="90%" height={20} data-testid="skeleton" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ py: 6, textAlign: 'center' }}>
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, index) => renderRow(item, index))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
